import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { API } from "@/App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Users, Trophy, Share2, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const ReferralPage = () => {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");

  const [settings, setSettings] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [myCode, setMyCode] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [referredForm, setReferredForm] = useState({ name: "", email: "" });
  const [generating, setGenerating] = useState(false);
  const [submittingRef, setSubmittingRef] = useState(false);
  const [refTracked, setRefTracked] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/referrals/settings`),
        axios.get(`${API}/referrals/leaderboard`),
      ]);
      setSettings(settingsRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch { /* ignore */ }

    if (refCode) {
      try {
        const res = await axios.get(`${API}/referrals/lookup/${refCode}`);
        setReferrerInfo(res.data);
      } catch { /* invalid code */ }
    }
  }, [refCode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/referrals/generate`, form);
      setMyCode(res.data);
      toast.success("Your referral link is ready!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate"); }
    finally { setGenerating(false); }
  };

  const handleTrackReferral = async () => {
    if (!referredForm.name.trim() || !referredForm.email.trim()) { toast.error("Name and email required"); return; }
    setSubmittingRef(true);
    try {
      const res = await axios.post(`${API}/referrals/track`, { code: refCode, name: referredForm.name, email: referredForm.email });
      if (res.data.already_referred) {
        toast.info("You were already referred — thank you!");
      } else {
        toast.success("Welcome! Your referral has been recorded.");
      }
      setRefTracked(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
    finally { setSubmittingRef(false); }
  };

  const copyLink = () => {
    if (myCode) {
      navigator.clipboard.writeText(`https://commonwealthfc.org/referral?ref=${myCode.code}`);
      toast.success("Link copied!");
    }
  };

  const shareLink = () => {
    if (myCode && navigator.share) {
      navigator.share({
        title: "Join Commonwealth FC",
        text: `Check out Commonwealth FC — Charlottesville's community-first youth soccer club! Use my referral link:`,
        url: `https://commonwealthfc.org/referral?ref=${myCode.code}`,
      }).catch(() => {});
    } else {
      copyLink();
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F3]">
      <Helmet>
        <title>{referrerInfo ? `${referrerInfo.name} invited you to` : "Refer a Friend to"} Commonwealth FC</title>
        <meta name="description" content="Refer families to Commonwealth FC youth soccer and earn rewards. Every referral helps keep soccer accessible for all Charlottesville families." />
        <meta property="og:title" content="Refer a Friend | Commonwealth FC" />
        <meta property="og:url" content="https://commonwealthfc.org/referral" />
        <link rel="canonical" href="https://commonwealthfc.org/referral" />
      </Helmet>

      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/join" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">Join</Link>
            <Link to="/" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">Home</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Referred landing (has ?ref= code) */}
        {refCode && referrerInfo && !refTracked ? (
          <div className="text-center mb-12" data-testid="referral-landing">
            <div className="inline-flex items-center gap-2 bg-[#C8E64E]/20 text-[#0C2E3C] px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Gift size={16} /> Referred by {referrerInfo.name}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0C2E3C] mb-4">
              WELCOME TO<br />COMMONWEALTH FC
            </h1>
            <p className="text-[#6B8A8F] text-base max-w-xl mx-auto mb-8">
              {referrerInfo.name} thinks your family would love our community-first youth soccer club. Enter your details below and both families earn rewards!
            </p>
            <div className="max-w-sm mx-auto space-y-3 text-left">
              <Input value={referredForm.name} onChange={(e) => setReferredForm((p) => ({ ...p, name: e.target.value }))} placeholder="Your name" className="rounded-none border-[#0C2E3C]/20" data-testid="referred-name" />
              <Input value={referredForm.email} onChange={(e) => setReferredForm((p) => ({ ...p, email: e.target.value }))} placeholder="Your email" type="email" className="rounded-none border-[#0C2E3C]/20" data-testid="referred-email" />
              <Button onClick={handleTrackReferral} disabled={submittingRef} className="w-full bg-[#C8E64E] hover:bg-[#b8d43e] text-[#0C2E3C] font-bold uppercase tracking-wider rounded-none h-12" data-testid="submit-referral-btn">
                {submittingRef ? <Loader2 size={16} className="animate-spin mr-2" /> : <ArrowRight size={16} className="mr-2" />}
                Join the Club
              </Button>
            </div>
          </div>
        ) : refTracked ? (
          <div className="text-center py-12" data-testid="referral-success">
            <CheckCircle2 size={56} className="mx-auto text-green-500 mb-4" />
            <h2 className="font-heading text-3xl font-bold text-[#0C2E3C] mb-3">You{"'"}re In!</h2>
            <p className="text-[#6B8A8F] mb-6">We{"'"}ve recorded your referral. Check your email for next steps on joining Commonwealth FC.</p>
            <Link to="/join"><Button className="bg-[#0C2E3C] text-[#C8E64E] font-bold uppercase rounded-none px-8 h-12">Register for Tryouts</Button></Link>
          </div>
        ) : (
          <>
            {/* Generate your own link */}
            <div className="text-center mb-12">
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0C2E3C] mb-4" data-testid="referral-page-title">
                REFER A FRIEND
              </h1>
              <p className="text-[#6B8A8F] text-base max-w-xl mx-auto">
                {settings?.reward_description || "Know a family who'd love Commonwealth FC? Share your unique link and earn rewards when they join!"}
              </p>
              {settings?.reward_amount && (
                <div className="inline-flex items-center gap-2 bg-[#C8E64E]/20 text-[#0C2E3C] px-4 py-2 rounded-full text-sm font-bold mt-4">
                  <Gift size={16} /> {settings.reward_amount}
                </div>
              )}
            </div>

            {!myCode ? (
              <div className="max-w-sm mx-auto bg-white border border-[#0C2E3C]/10 p-8" data-testid="generate-form">
                <h3 className="font-bold text-[#0C2E3C] mb-4 text-center">Get Your Referral Link</h3>
                <div className="space-y-3">
                  <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Your name" className="rounded-none border-[#0C2E3C]/20" data-testid="referrer-name" />
                  <Input value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="Your email" type="email" className="rounded-none border-[#0C2E3C]/20" data-testid="referrer-email" />
                  <Button onClick={handleGenerate} disabled={generating} className="w-full bg-[#0C2E3C] text-[#C8E64E] font-bold uppercase tracking-wider rounded-none h-12" data-testid="generate-link-btn">
                    {generating ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    Generate My Link
                  </Button>
                </div>
              </div>
            ) : (
              <div className="max-w-md mx-auto bg-white border border-[#C8E64E] p-8 text-center" data-testid="referral-link-card">
                <CheckCircle2 size={32} className="mx-auto text-green-500 mb-3" />
                <h3 className="font-bold text-[#0C2E3C] text-lg mb-2">Your Referral Link</h3>
                <div className="bg-[#F0F4F3] border border-[#0C2E3C]/10 p-3 mb-4 text-sm font-mono text-[#0C2E3C] break-all">
                  commonwealthfc.org/referral?ref={myCode.code}
                </div>
                <div className="flex gap-2 justify-center">
                  <Button onClick={copyLink} variant="outline" className="rounded-none border-[#0C2E3C]/20" data-testid="copy-referral-btn">
                    <Copy size={16} className="mr-1.5" /> Copy Link
                  </Button>
                  <Button onClick={shareLink} className="bg-[#0C2E3C] text-[#C8E64E] rounded-none" data-testid="share-referral-btn">
                    <Share2 size={16} className="mr-1.5" /> Share
                  </Button>
                </div>
                <p className="text-xs text-[#6B8A8F] mt-4">
                  Referrals so far: <strong>{myCode.referral_count || 0}</strong>
                </p>
              </div>
            )}
          </>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="mt-16" data-testid="referral-leaderboard">
            <h2 className="font-heading text-xl font-bold text-[#0C2E3C] text-center mb-6">TOP REFERRERS</h2>
            <div className="max-w-sm mx-auto space-y-2">
              {leaderboard.map((ref, i) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-[#0C2E3C]/10 px-4 py-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? "bg-[#C8E64E] text-[#0C2E3C]" : i === 1 ? "bg-gray-200 text-gray-600" : i === 2 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"
                  }`}>
                    {i + 1}
                  </span>
                  <span className="flex-1 font-medium text-sm text-[#0C2E3C]">{ref.name}</span>
                  <span className="text-sm font-bold text-[#0C2E3C]">{ref.referral_count}</span>
                  <Users size={14} className="text-[#6B8A8F]" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="bg-[#0C2E3C] mt-16 py-8 text-center">
        <p className="text-[#F0F4F3]/50 text-xs">Commonwealth FC &middot; Charlottesville, VA</p>
      </footer>
    </div>
  );
};

export default ReferralPage;
